/* Importing the necessary libraries and files */
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';

/* Importing the functions from the Redux slices. */
import { putDevice } from '../../features/device/deviceSlice';

/* Importing the functions from the utils folder. */
import handleFormResponse from '../../utils/handleFormResponse';
import { fDevice } from '../../utils/Device/Device.utils.format';
import { mDevice } from '../../utils/Device/Device.utils.map';

/* Defining the propTypes for the TelepreseceAddMarker component. */
TelepreseceAddMarker.propTypes = {
  device: PropTypes.object,
  setDevice: PropTypes.func,
  positionX: PropTypes.number,
  positionY: PropTypes.number,
  orientation: PropTypes.number,
};

/**
 * Renders a component for adding a marker in the telepresence section.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.device - The device object.
 * @param {function} props.setDevice - The function to set the device object.
 * @param {number} props.positionX - The X position of the marker.
 * @param {number} props.positionY - The Y position of the marker.
 * @param {number} props.orientation - The orientation of the marker.
 * @returns {JSX.Element} The rendered component.
 */
export default function TelepreseceAddMarker(props) {
  const { device, setDevice, positionX, positionY, orientation } = props;

  const dispatch = useDispatch();

  const [title] = useState('Add Position Marker');
  const [isAddMarkerOpen, setIsAddMarkerOpen] = useState(false);

  const handleAddMarker = () => {
    setIsAddMarkerOpen(true);
  };

  /* Creating a schema for the form. */
  const MybeshieMarkerSchema = Yup.object().shape({
    name: Yup.string().required('Name is required and must be unique.'),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: MybeshieMarkerSchema,
    onSubmit: async (values) => {
      const formValues = {
        ...device,
      };

      if (!formValues.markers) {
        formValues.markers = {};
      }

      if (values.name in formValues.markers) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Name must be unique.',
          showConfirmButton: true,
        });
        handleClose();
        return;
      }

      formValues.markers[values.name] = {
        positionX,
        positionY,
        orientation,
      };

      const deviceData = mDevice(formValues);
      await submitPutDevice(deviceData);
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  /**
   * It takes the values from the form, adds the deviceID to the values, and then sends the data to the
   * putDevice function.
   */
  const submitPutDevice = async (values) => {
    const data = { deviceID: device.id, data: values };
    const resultAction = await dispatch(putDevice(data));
    handleClose();
    const payload = await handleFormResponse({
      resultAction,
      successTitle: 'Marker added successfully!',
      navigateOnSuccess: false,
    });
    setDevice(fDevice(payload));
  };

  const handleClose = () => {
    setIsAddMarkerOpen(false);
  };

  const [expanded, setExpanded] = useState(false);
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      <Button variant="contained" onClick={handleAddMarker}>
        Add Marker
      </Button>

      <Dialog open={isAddMarkerOpen} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Name
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Name"
                        {...getFieldProps('name')}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Accordion
                    expanded={expanded === 'panel1'}
                    onChange={handleAccordionChange('panel1')}
                    sx={{ boxShadow: 3 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} id="panel1add-header">
                      <Typography sx={{ width: '33%', flexShrink: 0 }}>Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid item xs={12} md={12}>
                        <Stack spacing={3}>
                          <Stack spacing={2}>
                            <Typography variant="subtitle1" sx={{ pt: 1 }}>
                              Position and Orientation
                            </Typography>
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="X position"
                              type="number"
                              {...getFieldProps('positionX')}
                              value={positionX}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Y position"
                              type="number"
                              {...getFieldProps('maxslot')}
                              value={positionY}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Orientation"
                              type="number"
                              {...getFieldProps('orientation')}
                              value={orientation}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </Stack>
                        </Stack>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color="error" onClick={handleClose}>
                Cancel
              </Button>
              <LoadingButton type="submit" loading={isSubmitting}>
                Submit
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>
    </>
  );
}
